import {
  AgPublicEventType,
  ColDef,
  ColumnState,
  GridApi,
  GridState,
  GridStateKey,
} from "ag-grid-community"
import { useCallback, useRef, useSyncExternalStore } from "react"
import {
  CALENDAR_BLOCK_SENTINEL,
  isCalendarColumn,
  isCalendarOrderEntry,
  isStaleCalendarColumnId,
} from "~/utils/productTableUtils"
import { isAutoGroupColumn } from "~/utils/ag-utils"
import { filterDefined } from "~/utils/tsUtils"

import {
  type ColumnBook,
  newColumnBook,
  type StateVisibility,
  stateVisibility,
  takeNewColumns as takeNewBookColumns,
} from "./gridColumnBook"

const SELECTION_COLUMN_ID = "ag-Grid-SelectionColumn"

/** AG Grid's own columns (selection, auto group), never a user column. */
export const isAgGridInternalColumn = (colId: string) => colId.startsWith("ag-Grid-")

/**
 * Replace consecutive calendar columns in `ids` with a single
 * `CALENDAR_BLOCK_SENTINEL`. Non-calendar columns keep their order.
 */
function collapseCalendarColumns(ids: string[]): string[] {
  const result: string[] = []
  let inBlock = false
  for (const id of ids) {
    if (isCalendarColumn(id)) {
      if (!inBlock) {
        result.push(CALENDAR_BLOCK_SENTINEL)
        inBlock = true
      }
    } else {
      result.push(id)
      inBlock = false
    }
  }
  return result
}

function stripCalendarColumnsFromFilter(state: GridState): GridState {
  const filterState = state.filter as { filterModel?: Record<string, unknown> } | undefined
  const filterModel = filterState?.filterModel
  if (!filterModel) return state
  const cleaned = Object.fromEntries(
    Object.entries(filterModel).filter(([colId]) => !isCalendarColumn(colId)),
  )
  return {
    ...state,
    filter: { ...filterState, filterModel: cleaned },
  }
}

const MODEL_TYPE_BY_FILTER: Record<string, string> = {
  agNumberColumnFilter: "number",
  agTextColumnFilter: "text",
  agDateColumnFilter: "date",
  agSetColumnFilter: "set",
}

/**
 * Drops filter entries the column can no longer apply (filter disabled or type changed); ag-grid
 * would keep them with no UI to clear. Missing columns (`undefined`) keep theirs: may not exist yet.
 */
export function dropStaleFilterModelEntries(
  model: Record<string, { filterType?: string } | undefined>,
  columnFilter: (colId: string) => unknown,
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(model).filter(([colId, entry]) => {
      const filter = columnFilter(colId)
      if (filter === undefined) return true
      if (!filter) return false
      const expected = typeof filter === "string" ? MODEL_TYPE_BY_FILTER[filter] : undefined
      return !expected || !entry?.filterType || entry.filterType === expected
    }),
  )
}

// States we don't want
export const TRANSIENT_GRID_STATE_KEYS = [
  "rowGroupExpansion",
  "scroll",
  "rowSelection",
  "focusedCell",
  "rowPinning",
] as const

export const isExtradataColumn = (colId: string) =>
  colId.includes("(extradata)") ||
  colId.includes("(collection-extradata)") ||
  colId.includes("(incoming-order-extradata)")

type PersistedAggregation = { aggregationModel?: { colId: string; aggFunc: string }[] }

export function stripStaleAggregationModel(state: GridState): GridState {
  const aggregation = (state as { aggregation?: PersistedAggregation }).aggregation
  if (!aggregation) return state
  const extradataEntries = aggregation.aggregationModel?.filter((e) => isExtradataColumn(e.colId))
  if (extradataEntries && extradataEntries.length > 0) {
    return {
      ...state,
      aggregation: { aggregationModel: extradataEntries },
    } as GridState
  }
  const rest = { ...state } as Record<string, unknown>
  delete rest.aggregation
  return rest as GridState
}

/** The aggFunc a saved state's aggregation model names for each column. */
export function persistedAggFuncs(state: GridState | undefined): Map<string, string> {
  const model = (state?.aggregation as PersistedAggregation | undefined)?.aggregationModel ?? []
  return new Map(model.map((e) => [e.colId, e.aggFunc]))
}

/**
 * A full `setState`/`initialState` apply drops every column missing from its aggregation model,
 * which `stripStaleAggregationModel` removes, out of the value columns — the column can still
 * report its `aggFunc`, but group rows stop aggregating and render blank (SUP-421). Re-activates
 * each column's aggFunc: the persisted (extradata) one when `persisted` has it, else the colDef's.
 * Call after every full state apply, with the state that was applied.
 */
export function restoreColDefAggFuncs(api: GridApi, persisted?: GridState): void {
  const persistedAggs = persistedAggFuncs(persisted)
  const targets = filterDefined(
    (api.getColumns() ?? []).map((column) => {
      const aggFunc = persistedAggs.get(column.getColId()) ?? column.getColDef().aggFunc
      return aggFunc == null ? undefined : { column, aggFunc }
    }),
  )
  const state = targets
    .filter(({ column, aggFunc }) => column.getAggFunc() !== aggFunc)
    .map(({ column, aggFunc }) => ({ colId: column.getColId(), aggFunc }))
  if (state.length > 0) api.applyColumnState({ state })
  // applyColumnState does not re-activate a column whose function aggFunc is already set.
  const inactive = targets.filter(({ column }) => !column.isValueActive())
  if (inactive.length > 0) api.addValueColumns(inactive.map(({ column }) => column.getColId()))
}

/** A full `setState` plus the aggregation restore it needs. Use instead of a bare `setState`. */
export function setGridStateWithAggregation(api: GridApi, state: GridState): void {
  api.setState(state)
  restoreColDefAggFuncs(api, state)
}

/** The saved widths as column state, with flex cleared so `defaultColDef.flex` can't override them. */
export function savedWidthColumnState(state: GridState): ColumnState[] {
  return (state.columnSizing?.columnSizingModel ?? [])
    .filter((col) => col.width != null && col.colId !== SELECTION_COLUMN_ID)
    .map((col) => ({ colId: col.colId, width: col.width, flex: null }))
}

/** A registered column as a saved-state migration sees it, in definition order. */
export type SavedStateColumn = { colId: string; lockVisible?: boolean }

/**
 * Rewrites a saved or view state against the registered columns before the grid applies it.
 * `selectedColIds` are columns the table shows regardless of the state; empty when the state alone
 * decides, as for a view apply.
 */
export type SavedStateMigration = (
  state: GridState,
  columns: readonly SavedStateColumn[],
  selectedColIds: ReadonlySet<string>,
) => GridState

/** Rewrites a grid state about to be saved, given the state saved before it. */
export type SavedStateMerge = (next: GridState, previous: GridState | undefined) => GridState

/**
 * Re-activates the aggregation a full state apply dropped, given the state that was applied.
 * Defaults to {@link restoreColDefAggFuncs}.
 */
export type AggregationRestore = (api: GridApi, persisted?: GridState) => void

function savedStateColumns(
  defs: Iterable<readonly [string, Pick<ColDef, "lockVisible">]>,
): SavedStateColumn[] {
  return Array.from(defs, ([colId, def]) => ({ colId, lockVisible: def.lockVisible }))
}

type DefaultPinDef = Pick<ColDef, "pinned" | "initialPinned">

function defaultPin(def: DefaultPinDef): "left" | "right" | null {
  const pin = def.pinned === undefined ? def.initialPinned : def.pinned
  if (pin === true || pin === "left") return "left"
  return pin === "right" ? "right" : null
}

/**
 * Pins the columns a state has no entry for at their default pin. Applying a state unpins every
 * column its `columnPinning` leaves out, so a column added since the state was saved, or any
 * column of a state saved without a column order, would otherwise lose its default pin. A column
 * the state knows keeps the state's pin.
 */
export function pinUnsavedColumns(
  state: GridState,
  defs: Iterable<readonly [string, DefaultPinDef]>,
): GridState {
  const known = new Set([
    ...(state.columnOrder?.orderedColIds ?? []),
    ...(state.columnPinning?.leftColIds ?? []),
    ...(state.columnPinning?.rightColIds ?? []),
  ])
  const left: string[] = []
  const right: string[] = []
  for (const [colId, def] of defs) {
    if (known.has(colId)) continue
    const pin = defaultPin(def)
    if (pin === "left") left.push(colId)
    else if (pin === "right") right.push(colId)
  }
  if (left.length === 0 && right.length === 0) return state
  return {
    ...state,
    columnPinning: {
      leftColIds: [...(state.columnPinning?.leftColIds ?? []), ...left],
      rightColIds: [...(state.columnPinning?.rightColIds ?? []), ...right],
    },
  }
}

/** Moves every column, the group column included, back to its default pin. */
export function resetColumnPins(api: GridApi): void {
  const groupColumns = (api.getAllGridColumns() ?? []).filter(isAutoGroupColumn)
  api.applyColumnState({
    state: [...(api.getColumns() ?? []), ...groupColumns].map((column) => ({
      colId: column.getColId(),
      pinned: defaultPin(column.getColDef()),
    })),
  })
}

type ColumnDefaults = Pick<
  ColDef,
  "lockVisible" | "hide" | "initialHide" | "pinned" | "initialPinned"
>

function reconcileWithColumns(
  state: GridState,
  defs: Iterable<readonly [string, ColumnDefaults]>,
  migrate: SavedStateMigration | undefined,
  selectedColIds: ReadonlySet<string>,
): GridState {
  const pinned = pinUnsavedColumns(state, defs)
  return migrate ? migrate(pinned, savedStateColumns(defs), selectedColIds) : pinned
}

// strips the transient state from the grid state
export function stripTransientGridState(state?: GridState): GridState | undefined {
  if (!state) return state
  const sanitized = { ...state } as Record<string, unknown>
  TRANSIENT_GRID_STATE_KEYS.forEach((key) => {
    delete sanitized[key]
  })
  return stripStaleAggregationModel(sanitized as GridState)
}

// Ensures the selection column is always pinned to the left side of the grid.
export function ensureSelectionColumnPinned(state: GridState | undefined): GridState {
  const leftColIds = state?.columnPinning?.leftColIds ?? []
  const hasSelectionColumn = leftColIds.includes(SELECTION_COLUMN_ID)
  return {
    ...(state ?? {}),
    columnPinning: {
      leftColIds: hasSelectionColumn ? leftColIds : [SELECTION_COLUMN_ID, ...leftColIds],
      rightColIds: state?.columnPinning?.rightColIds ?? [],
    },
  }
}

/**
 * Strips flex from column sizing model entries that have explicit widths.
 * AG Grid returns both `width` and `flex` in getState(), but when `flex` is present
 * it overrides `width` on restore. By stripping flex we lock in the computed widths,
 * which is critical for the first-render flow: flex fills space → save widths → use widths on reload.
 */
function stripFlexFromColumnSizing(state: GridState): GridState {
  if (!state.columnSizing?.columnSizingModel) return state

  return {
    ...state,
    columnSizing: {
      ...state.columnSizing,
      columnSizingModel: state.columnSizing.columnSizingModel.map(
        (col: { colId: string; width?: number; flex?: number }) => {
          if (col.width != null) {
            // Column has a computed/explicit width — drop flex so width is used on restore
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { flex: _flex, ...rest } = col
            return rest
          }
          return col
        },
      ),
    },
  }
}

function filterColumnSizingModelCalendarColumns(stripped: GridState): GridState {
  if (!stripped.columnSizing?.columnSizingModel) return stripped
  return {
    ...stripped,
    columnSizing: {
      ...stripped.columnSizing,
      columnSizingModel: stripped.columnSizing.columnSizingModel.filter(
        (col: { colId: string }) => !isCalendarColumn(col.colId),
      ),
    },
  }
}

function collapseOrderedColIdsCalendarColumns(stripped: GridState): GridState {
  if (!stripped.columnOrder?.orderedColIds) return stripped
  return {
    ...stripped,
    columnOrder: {
      ...stripped.columnOrder,
      orderedColIds: collapseCalendarColumns(stripped.columnOrder.orderedColIds),
    },
  }
}

/**
 * Normalizes grid state for database saves
 * Strips transient state and calendar columns
 * Calendar columns are dynamically generated based on date ranges and should not be saved in views
 * Extradata columns (company-specific custom fields) ARE preserved so views restore their
 * ordering, sizing, and visibility.
 */
export function normalizeGridStateForSave(state: GridState): GridState {
  let stripped = stripTransientGridState(state)
  if (stripped) {
    delete (stripped as Record<string, unknown>).pagination
    stripped = stripCalendarColumnsFromFilter(stripped)

    // Filter calendar columns out of column sizing (their IDs change per render),
    // and collapse the run of calendar columns in `columnOrder.orderedColIds` to a
    // single `CALENDAR_BLOCK_SENTINEL` so we keep the calendar block's position
    // relative to non-calendar columns without persisting ephemeral week IDs.
    stripped = filterColumnSizingModelCalendarColumns(stripped)
    stripped = collapseOrderedColIdsCalendarColumns(stripped)

    // Strip flex from entries with explicit widths so saved widths take effect on restore
    stripped = stripFlexFromColumnSizing(stripped)
  }
  stripped = ensureSelectionColumnPinned(stripped)
  return stripped
}

function normalizeSizing(
  val: GridState["columnSizing"],
  hiddenColIds: Set<string>,
): GridState["columnSizing"] {
  const model = val?.columnSizingModel
  if (!model) return val
  return {
    columnSizingModel: model
      .filter((col) => !isCalendarColumn(col.colId) && !hiddenColIds.has(col.colId))
      .map((col) => ({
        colId: col.colId,
        width: col.width ? Math.round(col.width / 5) * 5 : col.width,
      })),
  }
}

function normalizeOrder(val: GridState["columnOrder"]): GridState["columnOrder"] {
  const ids = val?.orderedColIds
  if (!ids) return val
  // Collapse calendar runs to `CALENDAR_BLOCK_SENTINEL` so the comparison matches
  // what `normalizeGridStateForSave` persists (both sides agree on a single
  // positional marker for the calendar block).
  return { orderedColIds: collapseCalendarColumns(ids) }
}

/**
 * ag-grid's own envelope keys, not user layout. Typed against `GridStateKey` so an
 * ag-grid upgrade changing this list fails to compile instead of silently re-breaking
 * dirty detection.
 */
const AG_GRID_ENVELOPE_KEYS: readonly Exclude<keyof GridState, GridStateKey>[] = [
  "version",
  "partialColumnState",
  "rangeSelection",
]

/** Keys ignored in view dirty-detection: ag-grid's envelope keys plus `sideBar` (per-session tool-panel state). */
const DIRTY_COMPARE_IGNORED_KEYS: ReadonlySet<string> = new Set([
  ...AG_GRID_ENVELOPE_KEYS,
  "sideBar",
])

/**
 * Type-safe comparison of a single GridState property.
 * Applies per-key normalization and uses order-sensitive comparison for columnOrder/columnPinning.
 */
export function compareGridStateKey(
  key: keyof GridState,
  viewState: GridState,
  currentState: GridState,
  hiddenColIds: Set<string>,
): boolean {
  if (DIRTY_COMPARE_IGNORED_KEYS.has(key)) return true
  if (key === "columnSizing") {
    const viewVal = normalizeSizing(viewState.columnSizing, hiddenColIds)
    const currentVal = normalizeSizing(currentState.columnSizing, hiddenColIds)
    return JSON.stringify(sortObjectKeys(viewVal)) === JSON.stringify(sortObjectKeys(currentVal))
  }
  if (key === "columnOrder") {
    const viewVal = normalizeOrder(viewState.columnOrder)
    const currentVal = normalizeOrder(currentState.columnOrder)
    return JSON.stringify(viewVal) === JSON.stringify(currentVal)
  }
  if (key === "filter") {
    const normalizeFilterModel = (state: GridState) => {
      const model = (state.filter as { filterModel?: Record<string, unknown> } | undefined)
        ?.filterModel
      return model && Object.keys(model).length > 0 ? sortObjectKeys(model) : undefined
    }
    return (
      JSON.stringify(normalizeFilterModel(viewState)) ===
      JSON.stringify(normalizeFilterModel(currentState))
    )
  }
  if (key === "columnPinning") {
    return JSON.stringify(viewState.columnPinning) === JSON.stringify(currentState.columnPinning)
  }
  const viewVal = viewState[key]
  const currentVal = currentState[key]
  if (viewVal === undefined && currentVal === undefined) return true
  return JSON.stringify(sortObjectKeys(viewVal)) === JSON.stringify(sortObjectKeys(currentVal))
}

export function gridStatesEqual(
  viewState: GridState,
  currentState: GridState,
  hiddenColIds: Set<string>,
): boolean {
  const keys = new Set<keyof GridState>([
    ...(Object.keys(viewState) as (keyof GridState)[]),
    ...(Object.keys(currentState) as (keyof GridState)[]),
  ])
  for (const key of keys) {
    if (!compareGridStateKey(key, viewState, currentState, hiddenColIds)) return false
  }
  return true
}

/**
 * AG-Grid events that mutate the persisted `GridState` keys compared by
 * `gridStatesEqual` / `compareGridStateKey`. Used by `useGridDerivedValue` to
 * recompute a view's dirty state. Keep this aligned with the keys above:
 * sort, filter, columnOrder, columnSizing, columnPinning, columnVisibility,
 * rowGrouping, aggregation.
 */
export const PERSISTED_GRID_STATE_EVENTS: readonly AgPublicEventType[] = [
  "sortChanged",
  "filterChanged",
  "columnMoved",
  "columnResized",
  "columnPinned",
  "columnVisible",
  "columnRowGroupChanged",
  "columnValueChanged",
]

/**
 * The view's user-facing (buildable) column IDs, derived from its gridState. Excludes hidden
 * columns, ag-grid-internal columns (`ag-Grid-*`), and the calendar block/columns (those are
 * generated per page).
 */
export function deriveDisplayColumnsFromGridState(gridState: GridState | undefined): string[] {
  const orderedColIds = gridState?.columnOrder?.orderedColIds ?? []
  const hidden = new Set(gridState?.columnVisibility?.hiddenColIds ?? [])
  return orderedColIds.filter(
    (id) =>
      !hidden.has(id) &&
      !isAgGridInternalColumn(id) &&
      !isCalendarOrderEntry(id) &&
      !isStaleCalendarColumnId(id),
  )
}

/**
 * Recursively sorts object keys and arrays for consistent JSON comparison.
 * - String arrays are sorted alphabetically
 * - Object arrays with 'colId' property are sorted by colId
 * - Object keys are sorted alphabetically
 */
export function sortObjectKeys(obj: unknown): unknown {
  if (obj === null || typeof obj !== "object") {
    return obj
  }

  if (Array.isArray(obj)) {
    const processed = obj.map(sortObjectKeys)

    if (processed.length === 0) {
      return processed
    }

    if (typeof processed[0] === "string") {
      return [...processed].sort()
    }

    if (processed[0] && typeof processed[0] === "object" && "colId" in processed[0]) {
      return [...processed].sort((a, b) => {
        const aId = (a as { colId?: string }).colId ?? ""
        const bId = (b as { colId?: string }).colId ?? ""
        return aId.localeCompare(bId)
      })
    }

    return processed
  }

  return Object.keys(obj as Record<string, unknown>)
    .sort()
    .reduce((result: Record<string, unknown>, key) => {
      result[key] = sortObjectKeys((obj as Record<string, unknown>)[key])
      return result
    }, {})
}

// Registry of live ag-grid `GridApi` instances, keyed by tableId. Populated from the
// grid lifecycle (`DataTableV2` calls `setGridApi(event.api)` on `onGridReady` and
// `setGridApi(null)` on `onGridPreDestroyed`).
//
// Grid-API access convention — there are two ways to reach the same `GridApi`:
//   • `gridRef.current?.api` — synchronous IN-TREE access for the grid owner and close
//     descendants (the AG-Grid-React idiom). Use this when you hold the ref.
//   • this registry — for OUT-OF-TREE / reactive consumers that can't hold the ref
//     (e.g. CustomPagination, the ForecastUpdates badge, useView dirty-detection).
//     Entry points: `getGridApiById`, `useGridStateStorage().getGridApi`,
//     `useGridDerivedValue`, `subscribeGridApiChange`.
// Both resolve to the same instance — neither is a duplicate of the other.
// See docs/JSON_BLOB_VERSIONING.md § "Grid API access".
//
// Each entry also carries the table's saved-state migration: views apply state through a separate
// `useGridStateStorage` instance that doesn't know the table's options.
type RegisteredGrid = {
  api: GridApi
  migrateSavedState?: SavedStateMigration
  restoreAggregation?: AggregationRestore
  book: ColumnBook
}
const gridApiRegistry = new Map<string, RegisteredGrid>()
const registeredApi = (tableId: string) => gridApiRegistry.get(tableId)?.api
/** Ids of every column the grid has, in column definition order. */
export const liveColIds = (api: GridApi) =>
  (api.getColumns() ?? []).map((column) => column.getColId())

const initialStateKey = (tableId: string) => `initial-state-${tableId}`

function parseStoredState(raw: string): GridState | undefined {
  try {
    return stripTransientGridState(JSON.parse(raw))
  } catch {
    return undefined
  }
}

/**
 * Load grid state from sessionStorage
 */
function loadStateFromStorage(key: string): GridState | undefined {
  const raw = sessionStorage.getItem(key)
  return raw == null ? undefined : parseStoredState(raw)
}

// Keyed by the raw stored string: `stateColumnVisibility` reads it per selector snapshot, before
// the table's grid registers.
const persistedVisibilities = new Map<string, { raw: string; read: StateVisibility }>()

function persistedVisibility(tableId: string): StateVisibility {
  const raw = sessionStorage.getItem(initialStateKey(tableId))
  if (raw == null) return stateVisibility(undefined)
  const cached = persistedVisibilities.get(tableId)
  if (cached?.raw === raw) return cached.read
  const read = stateVisibility(parseStoredState(raw))
  persistedVisibilities.set(tableId, { raw, read })
  return read
}

/**
 * Save grid state to sessionStorage (internal use only)
 */
function saveStateToStorageInternal(key: string, state: GridState) {
  sessionStorage.setItem(key, JSON.stringify(state))
}

function removeStateFromStorageInternal(key: string) {
  sessionStorage.removeItem(key)
}

// Track when a view is being applied to prevent useEffects from overriding grid state
const applyingViewFlags = new Set<string>()
const applyingViewTimers = new Map<string, ReturnType<typeof setTimeout>>()
const applyingViewFallbackTimers = new Map<string, ReturnType<typeof setTimeout>>()

// Post-apply dirty-detection baseline, keyed by tableId. `setState(blob)` → `getState()`
// isn't an identity round-trip, so comparing live state against the raw DB blob can be
// permanently "dirty". Once an apply settles we snapshot the normalized live state instead.
const appliedViewBaselines = new Map<string, GridState>()

export function getAppliedViewBaseline(tableId: string): GridState | undefined {
  return appliedViewBaselines.get(tableId)
}

export function setAppliedViewBaseline(tableId: string, state: GridState) {
  appliedViewBaselines.set(tableId, state)
}

export function clearAppliedViewBaseline(tableId: string) {
  appliedViewBaselines.delete(tableId)
}

/** The persisted grid state's column order, as of the last save. */
export const persistedColumnOrder = (tableId: string): string[] | undefined =>
  loadStateFromStorage(initialStateKey(tableId))?.columnOrder?.orderedColIds

/**
 * Rewrites the table's persisted grid state, and the state its registered grid last applied, with
 * `update`. Nothing when no state is saved.
 */
export function updatePersistedState(tableId: string, update: (state: GridState) => GridState) {
  const state = loadStateFromStorage(initialStateKey(tableId))
  if (!state) return
  const next = update(state)
  saveStateToStorageInternal(initialStateKey(tableId), next)
  saveStateToStorageInternal(`grid-state-${tableId}`, next)
  const book = gridApiRegistry.get(tableId)?.book
  if (book?.applied) book.applied = update(book.applied)
}

/**
 * Column visibility as the table's state has it where its grid doesn't have the column: the state
 * the grid last applied, or before the grid registers, the persisted state it will start from.
 * The persisted state is read as stored, before the table's migration: no migration step changes
 * which listed columns are hidden.
 */
export function stateColumnVisibility(tableId: string): StateVisibility {
  const registered = gridApiRegistry.get(tableId)
  return registered ? stateVisibility(registered.book.applied) : persistedVisibility(tableId)
}

/** {@link takeNewBookColumns} on the table's registered grid; nothing before it registers. */
export function takeNewColumns(
  tableId: string,
  colIds: readonly string[],
  isLate: (colId: string) => boolean,
): ReturnType<typeof takeNewBookColumns> {
  const registered = gridApiRegistry.get(tableId)
  return registered ? takeNewBookColumns(registered.book, colIds, isLate) : { fresh: [] }
}

// Upper bound on the applying window, anchored to the apply itself: resolves the flag even
// when a no-op `setState` fires no events, and bounds how long a user edit right after an
// apply can be absorbed into the baseline.
const APPLYING_VIEW_SETTLE_CAP_MS = 500

function clearTimer(timers: Map<string, ReturnType<typeof setTimeout>>, tableId: string) {
  const timer = timers.get(tableId)
  if (timer) {
    clearTimeout(timer)
    timers.delete(tableId)
  }
}

function clearApplyingViewTimers(tableId: string) {
  clearTimer(applyingViewTimers, tableId)
  clearTimer(applyingViewFallbackTimers, tableId)
}

/**
 * Ends the applying window: snapshots the live state as the dirty-detection baseline and
 * clears the flag. No-op for plain user edits, where the applying flag was never set.
 */
function settleApplyingView(tableId: string) {
  clearApplyingViewTimers(tableId)
  if (!applyingViewFlags.has(tableId)) return
  applyingViewFlags.delete(tableId)
  const api = registeredApi(tableId)
  const state = api && !api.isDestroyed?.() ? api.getState() : undefined
  if (state) setAppliedViewBaseline(tableId, normalizeGridStateForSave(state))
  // Recompute dirty detection immediately.
  signalGridValueChange(tableId)
}

export function setIsApplyingView(tableId: string, value: boolean) {
  clearApplyingViewTimers(tableId)
  // New apply or abort (grid destroyed) — either way the previous baseline is stale.
  clearAppliedViewBaseline(tableId)
  if (value) {
    applyingViewFlags.add(tableId)
    const timer = setTimeout(() => settleApplyingView(tableId), APPLYING_VIEW_SETTLE_CAP_MS)
    applyingViewFallbackTimers.set(tableId, timer)
  } else {
    applyingViewFlags.delete(tableId)
  }
}

export function isApplyingView(tableId: string): boolean {
  return applyingViewFlags.has(tableId)
}

/**
 * Debounced settle: 200ms of stateUpdated quiet means AG Grid has finished processing
 * the applied state.
 */
export function debouncedResetIsApplyingView(tableId: string) {
  clearTimer(applyingViewTimers, tableId)
  const timer = setTimeout(() => settleApplyingView(tableId), 200)
  applyingViewTimers.set(tableId, timer)
}

// Simple pub/sub pattern for grid value change signals
type GridValueListener = () => void

const gridValueListeners = new Map<string, Set<GridValueListener>>()

// Pub/sub for grid API registration/unregistration changes
const gridApiListeners = new Map<string, Set<GridValueListener>>()

export function subscribeGridApiChange(tableId: string, listener: GridValueListener): () => void {
  if (!gridApiListeners.has(tableId)) gridApiListeners.set(tableId, new Set())
  const listeners = gridApiListeners.get(tableId)!
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
    if (listeners.size === 0) gridApiListeners.delete(tableId)
  }
}

function signalGridApiChange(tableId: string) {
  const listeners = gridApiListeners.get(tableId)
  if (listeners)
    Array.from(listeners).forEach((l) => {
      try {
        l()
      } catch {}
    })
}

// Pub/sub for view change events (back-to-default or view switch)
const viewChangeListeners = new Map<string, Set<GridValueListener>>()

export function subscribeViewChange(tableId: string, listener: GridValueListener): () => void {
  if (!viewChangeListeners.has(tableId)) viewChangeListeners.set(tableId, new Set())
  const listeners = viewChangeListeners.get(tableId)!
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
    if (listeners.size === 0) viewChangeListeners.delete(tableId)
  }
}

export function signalViewChange(tableId: string) {
  const listeners = viewChangeListeners.get(tableId)
  if (listeners) {
    Array.from(listeners).forEach((l) => {
      try {
        l()
      } catch {}
    })
  }
}

/**
 * Subscribe to a grid value change for a specific key.
 * Returns an unsubscribe function.
 */
export function subscribeGridValueChange(key: string, listener: GridValueListener): () => void {
  if (!gridValueListeners.has(key)) {
    gridValueListeners.set(key, new Set())
  }
  const listeners = gridValueListeners.get(key)!
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
    // Optional cleanup
    if (listeners.size === 0) {
      gridValueListeners.delete(key)
    }
  }
}

/**
 * Get grid API from registry by table ID (for use outside of hooks)
 */
export function getGridApiById<T = unknown>(tableId: string): GridApi<T> | undefined {
  return registeredApi(tableId) as GridApi<T> | undefined
}

const NO_GRID_EVENTS: readonly AgPublicEventType[] = []
const selectIsRegistered = (api: GridApi | undefined) => !!api && !api.isDestroyed()

export const useIsGridRegistered = (tableId: string): boolean =>
  useGridDerivedValue(tableId, selectIsRegistered, NO_GRID_EVENTS)

/**
 * `selector` over the table's registered grid, recomputed on `events`. A selector that builds a
 * value (an array, a set) passes `isEqual`: the previous value stands in while it holds, so the
 * component re-renders only when the value changes.
 */
export function useGridDerivedValue<R, T = unknown>(
  tableId: string,
  selector: (api: GridApi<T> | undefined) => R,
  events: readonly AgPublicEventType[],
  isEqual?: (previous: R, next: R) => boolean,
): R {
  const lastRef = useRef<{ value: R }>(null)
  const getSnapshot = useCallback(() => {
    const next = selector(registeredApi(tableId) as GridApi<T> | undefined)
    if (!isEqual) return next
    // eslint-disable-next-line react-hooks/refs -- useSyncExternalStore's snapshot cache: read and written in the same call.
    const last = lastRef.current
    if (last && isEqual(last.value, next)) return last.value
    lastRef.current = { value: next }
    return next
  }, [tableId, selector, isEqual])

  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      let detachEvents: (() => void) | undefined

      // AG Grid fires columnMoved/columnResized repeatedly during a drag with
      // finished:false; only react to the settled event so a drag doesn't cause a
      // recompute/re-render storm. Events without a drag phase have finished:undefined
      // and pass through unchanged.
      const handleEvent = (event: unknown) => {
        if ((event as { finished?: boolean } | undefined)?.finished === false) return
        onStoreChange()
      }

      const attach = () => {
        detachEvents?.()
        detachEvents = undefined
        const api = registeredApi(tableId)
        if (api && !api.isDestroyed?.()) {
          events.forEach((event) => api.addEventListener(event, handleEvent))
          detachEvents = () =>
            events.forEach((event) => api.removeEventListener(event, handleEvent))
        }
        onStoreChange()
      }

      const unsubscribeApi = subscribeGridApiChange(tableId, attach)
      // Baseline snapshots / save-time clears change derived values without an ag-grid event.
      const unsubscribeValue = subscribeGridValueChange(tableId, onStoreChange)
      attach()

      return () => {
        unsubscribeApi()
        unsubscribeValue()
        detachEvents?.()
      }
    },
    [tableId, events],
  )

  return useSyncExternalStore(subscribe, getSnapshot)
}

/**
 * Send a signal that will notify listeners of grid value change for the specific key.
 */
export function signalGridValueChange(key: string) {
  const listeners = gridValueListeners.get(key)
  if (listeners) {
    // Copy to array to prevent mutation during iteration
    Array.from(listeners).forEach((listener) => {
      try {
        listener()
      } catch (err) {
        console.error("Error in signalGridValueChange", err)
      }
    })
  }
}

type GridRegistration = {
  migrateSavedState?: SavedStateMigration
  restoreAggregation?: AggregationRestore
  /**
   * The columns the grid was created with, which AG Grid applied initialState to. ag-grid-react
   * applies columnDefs changes made before the grid is ready ahead of `gridReady`, so by then the
   * grid may hold columns that missed it. Unset, every column at registration counts as having had
   * it.
   */
  initialColIds?: ReadonlySet<string>
}

/**
 * Registers a table's grid api, with the saved-state migration and aggregation restore its applies
 * go through, or clears the registration when `api` is null.
 */
export function registerGridApi(
  tableId: string,
  api: GridApi | null,
  { migrateSavedState, restoreAggregation, initialColIds }: GridRegistration = {},
): void {
  if (api) {
    gridApiRegistry.set(tableId, {
      api,
      migrateSavedState,
      restoreAggregation,
      book: newColumnBook(api.getGridOption("initialState"), initialColIds ?? liveColIds(api)),
    })
  } else {
    gridApiRegistry.delete(tableId)
    // Clear applying-view flag and pending timer on grid destroy
    // to prevent the flag from getting stuck permanently
    setIsApplyingView(tableId, false)
  }
  signalGridApiChange(tableId)
}

/**
 * Persists `state` and applies it to the table's registered grid, migrated with the table's
 * migration. setState without a pagination key jumps to page 0; view gridStates never carry one.
 */
export function applyGridState(
  tableId: string,
  state: GridState,
  { keepPagination = false }: { keepPagination?: boolean } = {},
): void {
  const strippedState = stripTransientGridState(state) ?? state
  const {
    api,
    migrateSavedState: migrate,
    restoreAggregation: restore = restoreColDefAggFuncs,
  } = gridApiRegistry.get(tableId) ?? {}
  const liveDefs = (api?.getColumns() ?? []).map(
    (column) => [column.getColId(), column.getColDef()] as const,
  )
  const finalState = reconcileWithColumns(
    ensureSelectionColumnPinned(strippedState),
    liveDefs,
    api ? migrate : undefined,
    new Set(),
  )

  // Persist immediately so the state survives a page reload.
  // onStateUpdated fires async (debounced 500ms) and won't save before a reload.
  saveStateToStorageInternal(initialStateKey(tableId), stripFlexFromColumnSizing(finalState))

  if (api) {
    // Must run AFTER api.setState so all column IDs (incl. hidden extradata) are settled.
    const columnsWithWidths = savedWidthColumnState(finalState)

    setTimeout(() => {
      const registered = gridApiRegistry.get(tableId)
      if (registered?.api !== api || api.isDestroyed()) return
      registered.book = newColumnBook(finalState, liveColIds(api))
      api.setState(finalState, keepPagination ? ["pagination"] : undefined)
      if (columnsWithWidths.length > 0) {
        api.applyColumnState({ state: columnsWithWidths })
      }
      const defs = api.getGridOption("columnDefs")
      if (defs) api.setGridOption("columnDefs", [...defs])
      restore(api, finalState)
    }, 0)
  }
}

/**
 * Hook to manage grid state for AG-Grid tables
 */
export function useGridStateStorage<T = any>(
  id: string,
  {
    migrateSavedState,
    restoreAggregation,
    mergeSavedState,
  }: {
    migrateSavedState?: SavedStateMigration
    restoreAggregation?: AggregationRestore
    mergeSavedState?: SavedStateMerge
  } = {},
) {
  const gridApiId = id
  const gridStateKey = `grid-state-${gridApiId}`
  const initialStateId = initialStateKey(gridApiId)

  const getCurrentState = useCallback(() => {
    const api = registeredApi(gridApiId)
    return api?.getState()
  }, [gridApiId])

  /**
   * The persisted state. Given the table's colDefs (keyed by colId, in definition order), it is
   * migrated against them, with the columns the colDefs start shown kept shown.
   */
  const getInitialState = useCallback(
    (columnDefs?: ReadonlyMap<string, ColumnDefaults>) => {
      const state = loadStateFromStorage(initialStateId)
      if (!state) return state
      const stripped = stripStaleAggregationModel(state)
      if (!columnDefs) return stripped
      const selectedColIds = new Set(
        Array.from(columnDefs).flatMap(([colId, def]) =>
          (def.initialHide ?? def.hide) === false ? [colId] : [],
        ),
      )
      return reconcileWithColumns(stripped, columnDefs, migrateSavedState, selectedColIds)
    },
    [initialStateId, migrateSavedState],
  )

  const applyState = useCallback(
    (state: GridState, options?: { keepPagination?: boolean }) =>
      applyGridState(gridApiId, state, options),
    [gridApiId],
  )

  const saveState = useCallback(
    (state?: GridState) => {
      const merge = (next: GridState) =>
        mergeSavedState ? mergeSavedState(next, getInitialState()) : next
      // Strip flex from both sides so the comparison ignores flex differences —
      // we only care about actual width changes
      const currentState = stripFlexFromColumnSizing(
        merge(stripTransientGridState(getCurrentState()) ?? ({} as GridState)),
      )
      const initialState = stripFlexFromColumnSizing(
        stripTransientGridState(getInitialState()) ?? ({} as GridState),
      )

      if (JSON.stringify(currentState) === JSON.stringify(initialState)) return

      const api = registeredApi(gridApiId)
      const rawState = stripTransientGridState(state ?? api?.getState())
      if (!rawState) return

      // Strip flex before saving so computed widths are locked in
      // This is key for: flex fills space on first render → save widths → use widths on reload.
      // Also strip calendar-column filters: their colIds are ephemeral and index-based
      // (they shift with the month window), so persisting them re-applies stale filters
      // after refresh. Keep them session-only within the live grid instead.
      const stateToSave = merge(stripCalendarColumnsFromFilter(stripFlexFromColumnSizing(rawState)))

      saveStateToStorageInternal(gridStateKey, ensureSelectionColumnPinned(stateToSave))
      saveStateToStorageInternal(
        initialStateId,
        ensureSelectionColumnPinned(stateToSave ?? getInitialState()),
      )

      signalGridValueChange(gridApiId)
    },
    [gridApiId, getCurrentState, getInitialState, mergeSavedState],
  )

  const setGridApi = useCallback(
    (api: GridApi<T> | null, initialColIds?: ReadonlySet<string>) =>
      registerGridApi(gridApiId, api, { migrateSavedState, restoreAggregation, initialColIds }),
    [gridApiId, migrateSavedState, restoreAggregation],
  )

  const getGridApi = useCallback(
    () => registeredApi(gridApiId) as GridApi<T> | undefined,
    [gridApiId],
  )

  const clearState = useCallback(() => {
    removeStateFromStorageInternal(gridStateKey)
    removeStateFromStorageInternal(initialStateId)
    const registered = gridApiRegistry.get(gridApiId)
    if (registered) registered.book.applied = undefined

    const api = registeredApi(gridApiId)
    if (api && !api.isDestroyed?.()) {
      api.setFilterModel(null)
      resetColumnPins(api)
      api.sizeColumnsToFit()
    }

    signalGridValueChange(gridApiId)
  }, [gridApiId, gridStateKey, initialStateId])

  return {
    getCurrentState,
    getInitialState,
    setGridApi,
    applyState,
    saveState,
    getGridApi,
    clearState,
  }
}
